import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { getErrorTitle } from "../../../constants/constants";
import {
  ROLE_TYPE_BUSINESS_VENDOR,
  ROLE_TYPE_CAREGIVER,
  ROLE_TYPE_FAMILY_MEMBER,
  ROLE_TYPE_NURSE,
  ROLE_TYPE_PROVIDER,
} from "../../../constants/RegisterConstants";
import {
  appAccentPanelStyle,
  appDataTableWrapperStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appFullDialogStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";

type ResidentOption = {
  residentId: string;
  residentName: string;
};

type ShareTargetOption = {
  actorLinkId?: string;
  counterpartId: string;
  counterpartRoleType: string;
  displayName: string;
  displayEmail: string;
};

type RelationshipCenterDialogProps = {
  visible: boolean;
  onHide: () => void;
  currentRoleType: string;
  residentOptions?: ResidentOption[];
  initialResidentId?: string | null;
  onUnreadCountChange?: (count: number) => void;
  shareOnlyMode?: boolean;
};

const ROLE_LABELS: Record<string, string> = {
  [ROLE_TYPE_PROVIDER]: "Provider",
  [ROLE_TYPE_NURSE]: "Nurse",
  [ROLE_TYPE_CAREGIVER]: "Caregiver",
  [ROLE_TYPE_FAMILY_MEMBER]: "Family Member",
  [ROLE_TYPE_BUSINESS_VENDOR]: "Business Vendor",
  vendor: "Vendor",
};

const sectionCardStyle: React.CSSProperties = {
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  border: "1px solid #e2e8f0",
  borderRadius: "18px",
  padding: "1rem",
  boxShadow: "0 14px 32px rgba(15, 23, 42, 0.06)",
};

const sectionTitleRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.75rem",
  marginBottom: "0.85rem",
};

const countPillStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "1.9rem",
  height: "1.9rem",
  padding: "0 0.6rem",
  borderRadius: "999px",
  background: "#fff7ed",
  border: "1px solid #fdba74",
  color: "#9a3412",
  fontWeight: 700,
  fontSize: "0.82rem",
};

const isLikelyFullEmailAddress = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const getCanonicalRelationshipType = (firstRole: string, secondRole: string) =>
  [String(firstRole || "").trim().toLowerCase(), String(secondRole || "").trim().toLowerCase()]
    .filter(Boolean)
    .sort()
    .join("_");

const getRoleSearchOptions = (currentRoleType: string) => {
  const normalizedRole = String(currentRoleType || "").trim().toLowerCase();

  if (normalizedRole === ROLE_TYPE_PROVIDER) {
    return [
      { label: "Nurse", value: ROLE_TYPE_NURSE },
      { label: "Caregiver", value: ROLE_TYPE_CAREGIVER },
      { label: "Family Member", value: ROLE_TYPE_FAMILY_MEMBER },
      { label: "Business Vendor", value: ROLE_TYPE_BUSINESS_VENDOR },
    ];
  }

  if (normalizedRole === ROLE_TYPE_NURSE) {
    return [
      { label: "Provider", value: ROLE_TYPE_PROVIDER },
      { label: "Business Vendor", value: ROLE_TYPE_BUSINESS_VENDOR },
    ];
  }

  if (normalizedRole === ROLE_TYPE_BUSINESS_VENDOR) {
    return [
      { label: "Provider", value: ROLE_TYPE_PROVIDER },
      { label: "Nurse", value: ROLE_TYPE_NURSE },
      { label: "Caregiver", value: ROLE_TYPE_CAREGIVER },
    ];
  }

  return [{ label: "Provider", value: ROLE_TYPE_PROVIDER }];
};

const getStatusTone = (status: string) => {
  const normalizedStatus = String(status || "").toLowerCase();

  if (normalizedStatus === "approved" || normalizedStatus === "read") {
    return { background: "#dcfce7", color: "#166534", border: "#86efac" };
  }

  if (normalizedStatus === "pending" || normalizedStatus === "unread") {
    return { background: "#fff7ed", color: "#9a3412", border: "#fdba74" };
  }

  if (normalizedStatus === "rejected" || normalizedStatus === "deleted") {
    return { background: "#fee2e2", color: "#991b1b", border: "#fca5a5" };
  }

  return { background: "#e2e8f0", color: "#334155", border: "#cbd5e1" };
};

const StatusChip = ({ label }: { label: string }) => {
  const tone = getStatusTone(label);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "999px",
        padding: "0.3rem 0.7rem",
        fontSize: "0.78rem",
        fontWeight: 700,
        textTransform: "capitalize",
        background: tone.background,
        color: tone.color,
        border: `1px solid ${tone.border}`,
      }}
    >
      {String(label || "unknown").replaceAll("_", " ")}
    </span>
  );
};

const RelationshipCenterDialog = ({
  visible,
  onHide,
  currentRoleType,
  residentOptions = [],
  initialResidentId = null,
  onUnreadCountChange,
  shareOnlyMode = false,
}: RelationshipCenterDialogProps) => {
  const toast = useRef<Toast>(null);
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const currentActorId = authStore.getUserId();

  const searchRoleOptions = useMemo(() => getRoleSearchOptions(currentRoleType), [currentRoleType]);
  const defaultSearchRole = searchRoleOptions[0]?.value || ROLE_TYPE_NURSE;

  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [actorLinks, setActorLinks] = useState<any[]>([]);
  const [residentRoleLinks, setResidentRoleLinks] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [providerResidentOptions, setProviderResidentOptions] = useState<ResidentOption[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchRoleType, setSearchRoleType] = useState(defaultSearchRole);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedResidentId, setSelectedResidentId] = useState<string>(initialResidentId || "");
  const [selectedApprovedActorLinkId, setSelectedApprovedActorLinkId] = useState<string>("");
  const [selectedApprovedActorId, setSelectedApprovedActorId] = useState<string>("");
  const [selectedShareTarget, setSelectedShareTarget] = useState<ShareTargetOption | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [residentRequestError, setResidentRequestError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      return;
    }
    setSearchRoleType(defaultSearchRole);
  }, [defaultSearchRole, visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setSelectedResidentId(initialResidentId || "");
  }, [initialResidentId, visible]);

  useEffect(() => {
    onUnreadCountChange?.(notifications.filter((item: any) => !item.isRead).length);
  }, [notifications, onUnreadCountChange]);

  useEffect(() => {
    if (!visible || currentRoleType !== ROLE_TYPE_PROVIDER || residentOptions.length > 0) {
      if (!visible) {
        setProviderResidentOptions([]);
      }
      return;
    }

    const loadProviderResidents = async () => {
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getHomeListForProvider();
        const residentEntries = await Promise.all(
          (response?.homes || []).map(async (home: any) => {
            if (!home?.homeId) {
              return [] as ResidentOption[];
            }

            const residents = await backendAPIStore.getResidentsForHome(home.homeId);
            return (residents || [])
              .filter((resident: any) => resident?.residentId)
              .map((resident: any) => ({
                residentId: resident.residentId,
                residentName: `${resident.firstName || ""} ${resident.lastName || ""}`.trim() || "Resident",
              }));
          }),
        );

        setProviderResidentOptions(residentEntries.flat());
      } catch (error) {
        setProviderResidentOptions([]);
      }
    };

    void loadProviderResidents();
  }, [backendAPIStore, currentRoleType, residentOptions, visible]);

  const effectiveResidentOptions = residentOptions.length > 0 ? residentOptions : providerResidentOptions;

  const refreshRelationshipData = async () => {
    setLoading(true);
    try {
      await backendAPIStore.init();
      const [actorLinkResults, residentRoleLinkResults, notificationResults] = await Promise.all([
        backendAPIStore.getActorLinks(),
        backendAPIStore.getResidentRoleLinks(),
        backendAPIStore.getRelationshipNotifications(),
      ]);
      setActorLinks(actorLinkResults || []);
      setResidentRoleLinks(residentRoleLinkResults || []);
      setNotifications(notificationResults || []);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!visible) {
      return;
    }

    void refreshRelationshipData();
  }, [visible]);

  const approvedActorLinks = useMemo(() => {
    return actorLinks.filter((link: any) => String(link.status || "").toLowerCase() === "approved");
  }, [actorLinks]);

  const shareOnlyResidentRoleLinks = useMemo(() => {
    if (!shareOnlyMode) {
      return [];
    }

    return residentRoleLinks.filter((link: any) => {
      const status = String(link.status || "").toLowerCase();
      const linkedResidentId = String(link.providerResidentId || "").trim();
      return linkedResidentId === String(selectedResidentId || "").trim()
        && status !== "deleted";
    });
  }, [residentRoleLinks, selectedResidentId, shareOnlyMode]);

  const approvedActorLinkOptions = useMemo(() => {
    return approvedActorLinks.map((link: any) => {
      const counterpartId = link.requesterActorId === currentActorId ? link.targetActorId : link.requesterActorId;
      const counterpartRoleType = String(
        link.requesterActorId === currentActorId ? link.targetRoleType : link.requesterRoleType,
      ).toLowerCase();
      const displayName = link.requesterActorId === currentActorId
        ? (link.metadata?.targetDisplayName || counterpartId)
        : (link.metadata?.requesterDisplayName || counterpartId);
      const displayEmail = link.requesterActorId === currentActorId
        ? (link.metadata?.targetEmail || "")
        : (link.metadata?.requesterEmail || "");

      return {
        label: `${ROLE_LABELS[counterpartRoleType] || counterpartRoleType}${displayEmail ? ` - ${displayEmail}` : ""}`,
        value: link.linkId,
        counterpartId,
        counterpartRoleType,
        displayName,
        displayEmail,
      };
    });
  }, [approvedActorLinks, currentActorId]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (approvedActorLinkOptions.length === 0) {
      setSelectedApprovedActorLinkId("");
      setSelectedApprovedActorId("");
      return;
    }

    const matchedOption = approvedActorLinkOptions.find((option) => option.value === selectedApprovedActorLinkId);
    if (matchedOption) {
      setSelectedApprovedActorId(matchedOption.counterpartId);
      return;
    }

    setSelectedApprovedActorLinkId(approvedActorLinkOptions[0].value);
    setSelectedApprovedActorId(approvedActorLinkOptions[0].counterpartId);
  }, [approvedActorLinkOptions, selectedApprovedActorLinkId, visible]);

  const onSearch = async () => {
    const normalizedEmail = searchQuery.trim().toLowerCase();

    if (!normalizedEmail || !isLikelyFullEmailAddress(normalizedEmail)) {
      setSearchResults([]);
      setSearchError("Enter the full email address to search safely.");
      return;
    }

    setSearchError(null);
    setSearching(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.searchActors({
        email: normalizedEmail,
        roleType: searchRoleType,
      });
      const filtered = (response || []).filter((item: any) => item.userId !== currentActorId);
      setSearchResults(filtered || []);
      if (filtered.length === 0) {
        setSearchError(`No ${ROLE_LABELS[searchRoleType]?.toLowerCase() || searchRoleType} user matched that email address.`);
      }
    } catch (error: any) {
      setSearchError(error.message || "Unable to search users right now.");
    } finally {
      setSearching(false);
    }
  };

  const onCreateActorLink = async (user: any) => {
    try {
      await backendAPIStore.createActorLink({
        targetActorId: user.userId,
        targetRoleType: user.userRoleType,
        relationshipType: getCanonicalRelationshipType(currentRoleType, user.userRoleType),
        metadata: {
          requesterDisplayName: authStore.getUserName() || authStore.getUserEmailId() || "Current actor",
          requesterEmail: authStore.getUserEmailId() || "",
          targetDisplayName: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
          targetEmail: user.email || "",
          requesterRoleType: currentRoleType,
        },
      });
      toast.current?.show({
        severity: "success",
        summary: "Request sent",
        detail: "Relationship request sent successfully.",
        life: 2500,
      });
      setSearchResults([]);
      setSearchQuery("");
      await refreshRelationshipData();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3000 });
    }
  };

  const onPrepareResidentShare = (target: ShareTargetOption) => {
    setSelectedShareTarget(target);
    setSelectedApprovedActorLinkId(target.actorLinkId || "");
    setSelectedApprovedActorId(target.counterpartId);
    setResidentRequestError(null);
  };

  const onUpdateActorLink = async (link: any, status: string) => {
    try {
      await backendAPIStore.updateActorLinkStatus({
        linkId: link.linkId,
        status,
      });
      toast.current?.show({
        severity: "success",
        summary: "Updated",
        detail: `Relationship request ${status}.`,
        life: 2500,
      });
      await refreshRelationshipData();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3000 });
    }
  };

  const onCreateResidentRoleLink = async () => {
    if (!selectedResidentId) {
      setResidentRequestError("Choose a resident to share.");
      return;
    }

    const selectedResident = effectiveResidentOptions.find((resident) => resident.residentId === selectedResidentId);
    const selectedActorLink = approvedActorLinkOptions.find((option) => option.value === selectedApprovedActorLinkId);
    const resolvedTarget = selectedShareTarget || (selectedActorLink ? {
      actorLinkId: selectedActorLink.value,
      counterpartId: selectedActorLink.counterpartId,
      counterpartRoleType: selectedActorLink.counterpartRoleType,
      displayName: selectedActorLink.displayName,
      displayEmail: selectedActorLink.displayEmail,
    } : null);

    if (!selectedResident || !resolvedTarget) {
      setResidentRequestError("Choose who should receive this resident share.");
      return;
    }

    setResidentRequestError(null);
    try {
      await backendAPIStore.createResidentRoleLink({
        actorLinkId: resolvedTarget.actorLinkId || "",
        providerResidentId: selectedResident.residentId,
        linkedActorId: resolvedTarget.counterpartId,
        roleType: resolvedTarget.counterpartRoleType,
        metadata: {
          providerResidentName: selectedResident.residentName,
          linkedActorDisplayName: resolvedTarget.displayName,
          linkedActorEmail: resolvedTarget.displayEmail,
        },
      });
      toast.current?.show({
        severity: "success",
        summary: "Request sent",
        detail: "Resident access request sent successfully.",
        life: 2500,
      });
      setSelectedShareTarget(null);
      setSelectedApprovedActorLinkId("");
      setSelectedApprovedActorId("");
      await refreshRelationshipData();
    } catch (error: any) {
      setResidentRequestError(error.message || "Unable to send resident access request.");
    }
  };

  const onUpdateResidentRoleLink = async (link: any, status: string) => {
    try {
      await backendAPIStore.updateResidentRoleLinkStatus({
        linkId: link.linkId,
        status,
      });
      toast.current?.show({
        severity: "success",
        summary: "Updated",
        detail: `Resident access request ${status}.`,
        life: 2500,
      });
      await refreshRelationshipData();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3000 });
    }
  };

  const onMarkNotificationRead = async (notificationId: string) => {
    try {
      await backendAPIStore.updateRelationshipNotification({
        notificationId,
        markRead: true,
      });
      await refreshRelationshipData();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3000 });
    }
  };

  const actorLinkActionsBody = (link: any) => {
    const status = String(link.status || "").toLowerCase();
    const isIncoming = link.direction === "incoming";
    const displayName = isIncoming
      ? (link.metadata?.requesterDisplayName || link.requesterActorId || "Linked actor")
      : (link.metadata?.targetDisplayName || link.targetActorId || "Linked actor");
    const displayEmail = isIncoming
      ? (link.metadata?.requesterEmail || "")
      : (link.metadata?.targetEmail || "");
    const counterpartRoleType = String(link.direction === "incoming" ? link.requesterRoleType : link.targetRoleType || "").toLowerCase();

    return (
      <div className="flex flex-wrap gap-2">
        {status === "pending" && isIncoming ? (
          <>
            <Button type="button" label="Approve" severity="success" rounded size="small" onClick={() => onUpdateActorLink(link, "approved")} />
            <Button type="button" label="Reject" severity="danger" outlined rounded size="small" onClick={() => onUpdateActorLink(link, "rejected")} />
          </>
        ) : null}
        {currentRoleType === ROLE_TYPE_PROVIDER && status === "approved" ? (
          <Button
            type="button"
            label="Share Resident"
            severity="warning"
            rounded
            size="small"
            onClick={() => onPrepareResidentShare({
              actorLinkId: link.linkId,
              counterpartId: isIncoming ? link.requesterActorId : link.targetActorId,
              counterpartRoleType,
              displayName,
              displayEmail,
            })}
          />
        ) : null}
        {status !== "deleted" ? (
          <Button type="button" label="Remove" severity="secondary" outlined rounded size="small" onClick={() => onUpdateActorLink(link, "deleted")} />
        ) : null}
      </div>
    );
  };

  const residentRoleLinkActionsBody = (link: any) => {
    const status = String(link.status || "").toLowerCase();
    const isIncoming = link.direction === "incoming";

    return (
      <div className="flex flex-wrap gap-2">
        {status === "pending" && isIncoming ? (
          <>
            <Button type="button" label="Approve" severity="success" rounded size="small" onClick={() => onUpdateResidentRoleLink(link, "approved")} />
            <Button type="button" label="Reject" severity="danger" outlined rounded size="small" onClick={() => onUpdateResidentRoleLink(link, "rejected")} />
          </>
        ) : null}
        {status !== "deleted" ? (
          <Button type="button" label="Remove" severity="secondary" outlined rounded size="small" onClick={() => onUpdateResidentRoleLink(link, "deleted")} />
        ) : null}
      </div>
    );
  };

  const actorCounterpartBody = (link: any) => {
    const isIncoming = link.direction === "incoming";
    const displayName = isIncoming
      ? (link.metadata?.requesterDisplayName || link.requesterActorId || "Linked actor")
      : (link.metadata?.targetDisplayName || link.targetActorId || "Linked actor");
    const displayEmail = isIncoming
      ? (link.metadata?.requesterEmail || "")
      : (link.metadata?.targetEmail || "");
    const counterpartRoleType = String(link.direction === "incoming" ? link.requesterRoleType : link.targetRoleType || "").toLowerCase();

    return (
      <div>
        <div style={{ fontWeight: 700, color: "#0f172a" }}>{displayName}</div>
        <div style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>
          {ROLE_LABELS[counterpartRoleType] || counterpartRoleType || "Actor"}{displayEmail ? ` - ${displayEmail}` : ""}
        </div>
        <div className="mt-2">
          <StatusChip label={isIncoming ? "incoming" : "sent"} />
        </div>
      </div>
    );
  };

  const residentRoleLinkResidentBody = (link: any) => (
    <div>
      <div style={{ fontWeight: 700, color: "#0f172a" }}>{link.metadata?.providerResidentName || "Resident"}</div>
      <div style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>Resident access request</div>
    </div>
  );

  const residentRoleLinkTargetBody = (link: any) => (
    <div>
      <div style={{ fontWeight: 700, color: "#0f172a" }}>{link.metadata?.linkedActorDisplayName || "Linked actor"}</div>
      <div style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>
        {ROLE_LABELS[String(link.roleType || "").toLowerCase()] || link.roleType}
        {link.metadata?.linkedActorEmail ? ` - ${link.metadata.linkedActorEmail}` : ""}
      </div>
    </div>
  );

  const notificationBody = (rowData: any) => (
    <div>
      <div style={{ fontWeight: rowData.isRead ? 600 : 700, color: "#0f172a" }}>{rowData.title}</div>
      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.88rem", lineHeight: 1.5 }}>
        {rowData.message}
      </div>
    </div>
  );

  const unreadNotificationCount = notifications.filter((item: any) => !item.isRead).length;

  return (
    <Dialog
      visible={visible}
      modal
      header={renderSurfaceHeader(
        shareOnlyMode ? "Share Resident Access" : "Relationship Center",
        shareOnlyMode
          ? "Search by full email address, choose who should receive access, and send the resident share in one place."
          : "Manage role connections, resident sharing, and recent relationship activity.",
        "pi pi-share-alt",
      )}
      style={shareOnlyMode ? { width: "min(94vw, 56rem)" } : appFullDialogStyle}
      headerStyle={appDialogHeaderStyle}
      contentStyle={appDialogContentStyle}
      onHide={onHide}
    >
      <Toast ref={toast} />
      <div className={shareOnlyMode ? "flex justify-content-center" : "grid"}>
        {!shareOnlyMode ? (
          <div className="col-12 lg:col-5">
            <div style={appAccentPanelStyle} className="mb-3">
              <div style={{ fontWeight: 700, color: "#9a3412" }}>Find another actor</div>
              <div className="mt-2" style={appMutedTextStyle}>
                Search by full email address to either build a connection or share resident access directly.
              </div>
            </div>
            <div style={appFormSectionStyle}>
              <div className="field">
                <label className="mb-2 block">Role</label>
                <Dropdown
                  options={searchRoleOptions}
                  optionLabel="label"
                  optionValue="value"
                  value={searchRoleType}
                  onChange={(e) => setSearchRoleType(e.value)}
                  style={{ width: "100%" }}
                />
              </div>
              <div className="field">
                <label className="mb-2 block">Search</label>
                <InputText
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter full email address"
                  style={{ width: "100%" }}
                />
              </div>
              {searchError ? <div className="p-error mb-3">{searchError}</div> : null}
              <Button type="button" label="Search" severity="warning" rounded loading={searching} onClick={onSearch} />

              {searchResults.length > 0 ? (
                <div className="mt-4" style={{ ...appDataTableWrapperStyle, borderRadius: "16px", overflow: "hidden" }}>
                  <DataTable value={searchResults} size="small" emptyMessage="No matching users found.">
                    <Column
                      header="Match"
                      body={(rowData: any) => (
                        <div>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>
                            {`${rowData.firstName || ""} ${rowData.lastName || ""}`.trim() || rowData.email}
                          </div>
                          <div style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>
                            {ROLE_LABELS[String(rowData.userRoleType || "").toLowerCase()] || rowData.userRoleType} - {rowData.email}
                          </div>
                        </div>
                      )}
                    />
                    <Column
                      header=""
                      body={(rowData: any) => (
                        <div className="flex flex-wrap gap-2 justify-content-end">
                          <Button type="button" label="Connect" severity="warning" rounded size="small" onClick={() => onCreateActorLink(rowData)} />
                          {currentRoleType === ROLE_TYPE_PROVIDER ? (
                            <Button
                              type="button"
                              label="Share Resident"
                              severity="secondary"
                              outlined
                              rounded
                              size="small"
                              onClick={() => onPrepareResidentShare({
                                counterpartId: rowData.userId,
                                counterpartRoleType: String(rowData.userRoleType || "").toLowerCase(),
                                displayName: `${rowData.firstName || ""} ${rowData.lastName || ""}`.trim() || rowData.email || "AFH Hub user",
                                displayEmail: rowData.email || "",
                              })}
                            />
                          ) : null}
                        </div>
                      )}
                      style={{ width: currentRoleType === ROLE_TYPE_PROVIDER ? "17rem" : "9rem" }}
                    />
                  </DataTable>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className={shareOnlyMode ? "" : "col-12 lg:col-7"} style={shareOnlyMode ? { width: "100%", maxWidth: "48rem" } : undefined}>
          {currentRoleType === ROLE_TYPE_PROVIDER && effectiveResidentOptions.length > 0 ? (
            <div style={shareOnlyMode ? { ...sectionCardStyle, padding: "1.15rem" } : sectionCardStyle} className={shareOnlyMode ? "" : "mb-4"}>
              <div style={appAccentPanelStyle} className="mb-3">
                <div style={{ fontWeight: 700, color: "#9a3412" }}>Share a resident</div>
                <div className="mt-2" style={appMutedTextStyle}>
                  Choose a resident, then search by the other actor's full email address or pick someone from connected users. AFH Hub will create the actor link behind the scenes when needed.
                </div>
              </div>
              <div style={appFormSectionStyle}>
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <label className="mb-2 block">Resident</label>
                    <Dropdown
                      options={effectiveResidentOptions.map((resident) => ({ label: resident.residentName, value: resident.residentId }))}
                      value={selectedResidentId}
                      onChange={(e) => setSelectedResidentId(e.value)}
                      placeholder="Choose a resident"
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="col-12 md:col-6">
                    <label className="mb-2 block">Role</label>
                    <Dropdown
                      options={searchRoleOptions}
                      value={searchRoleType}
                      onChange={(e) => setSearchRoleType(e.value)}
                      placeholder="Role"
                      style={{ width: "100%" }}
                      disabled={!!selectedShareTarget}
                    />
                  </div>
                  <div className="col-12">
                    <label className="mb-2 block">Share with</label>
                    {selectedShareTarget ? (
                      <div style={{ border: "1px solid #e2e8f0", borderRadius: "14px", padding: "0.95rem", background: "#ffffff" }}>
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>{selectedShareTarget.displayName}</div>
                        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>
                          {ROLE_LABELS[selectedShareTarget.counterpartRoleType] || selectedShareTarget.counterpartRoleType}
                          {selectedShareTarget.displayEmail ? ` - ${selectedShareTarget.displayEmail}` : ""}
                        </div>
                        <div className="mt-2">
                          <StatusChip label={selectedShareTarget.actorLinkId ? "connected user" : "direct share"} />
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2 justify-content-end">
                          <Button type="button" label="Choose Someone Else" severity="secondary" outlined rounded size="small" onClick={() => setSelectedShareTarget(null)} />
                          <Button type="button" label="Send Share Request" severity="warning" rounded size="small" onClick={onCreateResidentRoleLink} />
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-column gap-3">
                        <div className="grid align-items-end">
                          <div className="col-12 md:col-8">
                            <label className="mb-2 block" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>Full email address</label>
                            <InputText
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              placeholder="Enter full email address"
                              style={{ width: "100%" }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  void onSearch();
                                }
                              }}
                            />
                          </div>
                          <div className="col-12 md:col-4">
                            <Button
                              type="button"
                              label="Search"
                              severity="secondary"
                              outlined
                              rounded
                              onClick={() => { void onSearch(); }}
                              loading={searching}
                              style={{ width: "100%" }}
                            />
                          </div>
                        </div>

                        {searchResults.length > 0 ? (
                          <div style={{ border: "1px solid #e2e8f0", borderRadius: "14px", background: "#ffffff", overflow: "hidden" }}>
                            {searchResults.slice(0, 5).map((user: any, index: number) => (
                              <div
                                key={user.userId}
                                style={{
                                  padding: "0.85rem 0.95rem",
                                  borderBottom: index === Math.min(searchResults.length, 5) - 1 ? "none" : "1px solid #e2e8f0",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: "0.75rem",
                                  flexWrap: "wrap",
                                }}
                              >
                                <div style={{ minWidth: 0, flex: "1 1 16rem" }}>
                                  <div style={{ fontWeight: 700, color: "#0f172a" }}>
                                    {`${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || user.userId}
                                  </div>
                                  <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.88rem", wordBreak: "break-word" }}>
                                    {ROLE_LABELS[user.userRoleType] || user.userRoleType}
                                    {user.email ? ` - ${user.email}` : ""}
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  label="Share Resident"
                                  severity="warning"
                                  rounded
                                  outlined
                                  size="small"
                                  onClick={() => onPrepareResidentShare({
                                    counterpartId: user.userId,
                                    counterpartRoleType: String(user.userRoleType || "").toLowerCase(),
                                    displayName: `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || user.userId,
                                    displayEmail: user.email || "",
                                  })}
                                />
                              </div>
                            ))}
                          </div>
                        ) : null}

                        {searchError ? <div className="p-error">{searchError}</div> : null}

                        {approvedActorLinkOptions.length > 0 && !shareOnlyMode ? (
                          <div className="grid align-items-end">
                            <div className="col-12 md:col-8">
                              <label className="mb-2 block" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>Or choose from connected users</label>
                              <Dropdown
                                options={approvedActorLinkOptions}
                                optionLabel="label"
                                optionValue="value"
                                value={selectedApprovedActorLinkId}
                                onChange={(e) => {
                                  setSelectedApprovedActorLinkId(e.value);
                                  const selectedOption = approvedActorLinkOptions.find((option) => option.value === e.value);
                                  setSelectedApprovedActorId(selectedOption?.counterpartId || "");
                                }}
                                placeholder="Choose a connected user"
                                style={{ width: "100%" }}
                              />
                            </div>
                            <div className="col-12 md:col-4">
                              <Button
                                type="button"
                                label="Use Connected User"
                                severity="secondary"
                                outlined
                                rounded
                                style={{ width: "100%" }}
                                onClick={() => {
                                  const selectedOption = approvedActorLinkOptions.find((option) => option.value === selectedApprovedActorLinkId);
                                  if (selectedOption) {
                                    onPrepareResidentShare({
                                      actorLinkId: selectedOption.value,
                                      counterpartId: selectedOption.counterpartId,
                                      counterpartRoleType: selectedOption.counterpartRoleType,
                                      displayName: selectedOption.displayName,
                                      displayEmail: selectedOption.displayEmail,
                                    });
                                  }
                                }}
                              />
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
                {residentRequestError ? <div className="p-error mt-3">{residentRequestError}</div> : null}
              </div>
            </div>
          ) : null}
          {shareOnlyMode && selectedResidentId ? (
            <div style={{ ...sectionCardStyle, marginTop: "1rem", padding: "1.15rem" }}>
              <div className="flex justify-content-between align-items-center gap-3 mb-3">
                <div>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Shared With</div>
                  <div style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>
                    People who currently have this resident access request or active access.
                  </div>
                </div>
                <span style={countPillStyle}>{shareOnlyResidentRoleLinks.length}</span>
              </div>

              {shareOnlyResidentRoleLinks.length === 0 ? (
                <div style={{ ...appMutedTextStyle, padding: "0.25rem 0" }}>
                  This resident has not been shared with anyone yet.
                </div>
              ) : (
                <div className="flex flex-column gap-3">
                  {shareOnlyResidentRoleLinks.map((link: any) => (
                    <div
                      key={link.linkId}
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "14px",
                        padding: "0.95rem",
                        background: "#ffffff",
                      }}
                    >
                      <div className="flex flex-wrap justify-content-between align-items-start gap-3">
                        <div style={{ minWidth: 0, flex: "1 1 18rem" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>
                            {link.metadata?.linkedActorDisplayName || "Linked actor"}
                          </div>
                          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.88rem", wordBreak: "break-word" }}>
                            {ROLE_LABELS[String(link.roleType || "").toLowerCase()] || link.roleType}
                            {link.metadata?.linkedActorEmail ? ` - ${link.metadata.linkedActorEmail}` : ""}
                          </div>
                          <div className="mt-2">
                            <StatusChip label={String(link.status || "pending")} />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 justify-content-end">
                          {residentRoleLinkActionsBody(link)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
          {!shareOnlyMode ? (
            <>
              <div style={sectionCardStyle} className="mb-4">
                <div style={sectionTitleRowStyle}>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Notifications</div>
                    <div style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>Recent relationship updates</div>
                  </div>
                  <span style={countPillStyle}>{unreadNotificationCount}</span>
                </div>
                <div style={{ ...appDataTableWrapperStyle, border: "none", boxShadow: "none", padding: 0 }}>
                  <DataTable value={notifications} size="small" loading={loading} emptyMessage="No notifications yet.">
                    <Column header="Update" body={notificationBody} />
                    <Column
                      header=""
                      body={(rowData: any) => (
                        !rowData.isRead ? (
                          <Button type="button" label="Mark Read" severity="secondary" outlined rounded size="small" onClick={() => onMarkNotificationRead(rowData.notificationId)} />
                        ) : (
                          <StatusChip label="read" />
                        )
                      )}
                      style={{ width: "9rem" }}
                    />
                  </DataTable>
                </div>
              </div>

              <div style={sectionCardStyle} className="mb-4">
                <div style={sectionTitleRowStyle}>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Actor Relationships</div>
                    <div style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>Approved and pending role connections</div>
                  </div>
                  <span style={countPillStyle}>{actorLinks.length}</span>
                </div>
                <div style={{ ...appDataTableWrapperStyle, border: "none", boxShadow: "none", padding: 0 }}>
                  <DataTable value={actorLinks} size="small" loading={loading} emptyMessage="No relationship requests yet.">
                    <Column header="Actor" body={actorCounterpartBody} />
                    <Column header="Status" body={(rowData: any) => <StatusChip label={rowData.status} />} style={{ width: "8rem" }} />
                    <Column header="Actions" body={actorLinkActionsBody} style={{ width: "13rem" }} />
                  </DataTable>
                </div>
              </div>

              <div style={sectionCardStyle}>
                <div style={sectionTitleRowStyle}>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Resident Access</div>
                    <div style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>Resident sharing requests and approvals</div>
                  </div>
                  <span style={countPillStyle}>{residentRoleLinks.length}</span>
                </div>
                <div style={{ ...appDataTableWrapperStyle, border: "none", boxShadow: "none", padding: 0 }}>
                  <DataTable value={residentRoleLinks} size="small" loading={loading} emptyMessage="No resident access requests yet.">
                    <Column header="Resident" body={residentRoleLinkResidentBody} />
                    <Column header="Linked Actor" body={residentRoleLinkTargetBody} />
                    <Column header="Status" body={(rowData: any) => <StatusChip label={rowData.status} />} style={{ width: "8rem" }} />
                    <Column header="Actions" body={residentRoleLinkActionsBody} style={{ width: "13rem" }} />
                  </DataTable>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
};

export default RelationshipCenterDialog;
